import { Text } from "@/components/podkit/typography/Text";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SubjectDisplay } from "@/components/SubjectDisplay";
import { WebhookProvider } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { useState, type FC } from "react";
import type { PlainWebhook } from "@/queries/webhook-queries";
import { Link } from "react-router-dom";
import { webhookDetailsUrl } from "@/utils/automations";
import { pluralize } from "@/utils/strings";
import { WEBHOOK_PROVIDER_CONFIG } from "@/components/webhooks/webhook-config";
import { WebhookActionsDropdown } from "@/components/webhooks/WebhookActionsDropdown";

type WebhookListItemProps = PropsWithClassName<{
    webhook: PlainWebhook;
}>;

export const WebhookListItem: FC<WebhookListItemProps> = ({ webhook, className }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const name = webhook.metadata?.name || "Unnamed webhook";
    const creator = webhook.metadata?.creator;
    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const boundWorkflowCount = webhook.boundWorkflowCount;
    const ProviderIcon = WEBHOOK_PROVIDER_CONFIG[provider].icon;

    return (
        <li
            className={cn("flex items-start gap-3", className)}
            data-testid="webhook-list-item"
        >
            <Link
                to={webhookDetailsUrl(webhook.id)}
                state={{ canGoBack: true }}
                className={cn(
                    "flex min-w-0 flex-1 items-start gap-3 rounded-lg p-3",
                    "hover:bg-surface-secondary",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    { "bg-surface-secondary": isDropdownOpen },
                )}
                data-tracking-id="webhook-list-item"
            >
                <ProviderIcon size="base" className="shrink-0" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <Text className="truncate text-base font-medium">{name}</Text>
                    <Text className="text-sm text-content-secondary">Never been used</Text>
                    <div className="flex items-center gap-1 text-sm text-content-secondary">
                        <SubjectDisplay subject={creator} />
                        <span className="text-content-muted">·</span>
                        <Text>
                            {boundWorkflowCount} {pluralize(boundWorkflowCount, "automation")}
                        </Text>
                    </div>
                </div>
            </Link>
            <div className="p-3">
                <WebhookActionsDropdown webhook={webhook} buttonSize="sm" onOpenChange={setIsDropdownOpen} />
            </div>
        </li>
    );
};
